import React, { useState, useEffect } from 'react'
import { 
  Zap, 
  Code, 
  ArrowRight, 
  Loader2, 
  Bug, 
  Copy,
  Download,
  RefreshCw,
  Settings,
  ChevronDown,
  Play,
  Sparkles,
  Clock,
  Cpu,
  Layers
} from 'lucide-react'
import { AIService } from '../services/aiService'
import { AIServiceEnhanced } from '../services/aiServiceEnhanced'
import { checkBackendHealth } from '../utils/healthCheck'
import { useZenSettings } from '../hooks/useZenSettings'
import { useProviders } from '../hooks/useProviders'
import ProviderSelector from './chat/ProviderSelector'
import ZenSimpleDebugPanel, { ApiCall } from './ZenSimpleDebugPanel'
import { SyntaxLensWidget } from './SyntaxLensWidget'
import { codeSamples } from '../data/codeSamples'

interface ZenCreatorSimpleMOCKProps {
  className?: string
}

const ZenCreatorSimpleMOCK: React.FC<ZenCreatorSimpleMOCKProps> = ({ className = '' }) => {
  const [sourceLanguage, setSourceLanguage] = useState('Python')
  const [targetLanguage, setTargetLanguage] = useState('JavaScript')
  const [isGenerating, setIsGenerating] = useState(false)
  const [showOutput, setShowOutput] = useState(false) // Changed to false initially
  const [activeTab, setActiveTab] = useState('lens')
  const [leftPanelWidth, setLeftPanelWidth] = useState(25) // Percentage
  const [isDragging, setIsDragging] = useState(false)
  const [code, setCode] = useState('') // Add code state
  const [generatedHTML, setGeneratedHTML] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [aiService, setAIService] = useState<AIService | null>(null)
  const [aiServiceEnhanced, setAIServiceEnhanced] = useState<AIServiceEnhanced | null>(null)
  const [isDebugOpen, setIsDebugOpen] = useState(false)
  const [apiCalls, setApiCalls] = useState<ApiCall[]>([])
  const [isLanguageSelectorOpen, setIsLanguageSelectorOpen] = useState(false)
  const [isSampleSelectorOpen, setIsSampleSelectorOpen] = useState(false)
  const [selectedSample, setSelectedSample] = useState('fibonacci')
  
  const { getPreferredProvider } = useZenSettings()
  const { providers, currentProvider, switchProvider, isLoadingProviders } = useProviders()

  const languages = [
    { code: 'Python', icon: '🐍' },
    { code: 'JavaScript', icon: '⚡' },
    { code: 'TypeScript', icon: '📘' },
    { code: 'C', icon: '⚙️' },
    { code: 'C++', icon: '🔧' },
    { code: 'Rust', icon: '🦀' },
    { code: 'Go', icon: '🐹' },
    { code: 'Java', icon: '☕' },
    { code: 'Swift', icon: '🍎' },
    { code: 'Ruby', icon: '💎' }
  ]


  // Get current sample for selected language
  const getCurrentSample = () => {
    const sample = codeSamples[selectedSample as keyof typeof codeSamples]
    const langKey = targetLanguage as keyof typeof sample.code
    return sample.code[langKey] || sample.code.JavaScript
  }
  
  // Initialize backend services
  useEffect(() => {
    const initService = async () => {
      const available = await checkBackendHealth()
      if (available) {
        setAIService(new AIService())
        setAIServiceEnhanced(new AIServiceEnhanced())
      } else {
        setError('Backend server not available. Please start it with: npm run start:backend')
      }
    }
    initService()
    
    // Check URL parameter for debug flag
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('debug') === 'true') {
      setIsDebugOpen(true)
    }
  }, [])
  
  // Load sample code when target language or selected sample changes
  useEffect(() => {
    setCode(getCurrentSample())
  }, [targetLanguage, selectedSample])
  
  // Auto-select default provider if none is currently selected
  useEffect(() => {
    if (!currentProvider && providers.length > 0) {
      const preferredProvider = getPreferredProvider(providers)
      if (preferredProvider) {
        switchProvider(preferredProvider)
      }
    }
  }, [currentProvider, providers, getPreferredProvider, switchProvider])

  const handleGenerate = async () => {
    if (!aiService || !aiServiceEnhanced) {
      setError('Backend service not initialized')
      return
    }
    
    if (!code.trim()) {
      setError('Please enter some code to analyze')
      return
    }
    
    setIsGenerating(true)
    setError(null)
    setGeneratedHTML(null)
    
    // Create API call tracking object
    const callId = `call-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`
    const startTime = Date.now()
    const apiCall: ApiCall = {
      id: callId,
      timestamp: new Date(),
      request: {
        type: 'lens-html',
        sourceLanguage,
        targetLanguage,
        code,
        provider: currentProvider,
        url: import.meta.env.VITE_API_BASE_URL || '',  // Use Vite proxy in dev
        headers: {
          'Content-Type': 'application/json'
        }
      },
      status: 'pending'
    }
    
    // Add to tracking
    setApiCalls(prev => [...prev, apiCall])
    
    try {
      // Use current selected provider or get preferred provider
      const selectedProvider = currentProvider || getPreferredProvider(providers)
      
      // Generate HTML with lens insights using enhanced service for complete response capture
      const enhancedResult = await aiServiceEnhanced.generateLensHTMLWithDetails(
        code,
        sourceLanguage, // The language we know (insights will be in this context)
        targetLanguage, // The code language (what we're analyzing)
        undefined, // Let backend determine concept from code
        selectedProvider // Pass the selected provider with model
      )
      
      const endTime = Date.now()
      
      // Update API call with success and complete response details from enhanced service
      setApiCalls(prev => prev.map(call => 
        call.id === callId 
          ? {
              ...call,
              response: {
                status: enhancedResult.status,
                statusText: enhancedResult.statusText,
                data: enhancedResult.html,
                headers: enhancedResult.responseHeaders,
                metadata: {
                  title: enhancedResult.title,
                  description: enhancedResult.description,
                  responseTime: endTime - startTime,
                  model: selectedProvider?.model,
                  provider: selectedProvider?.provider
                },
                rawResponse: enhancedResult.rawResponse || enhancedResult,
                aiProviderDetails: enhancedResult.aiProviderDetails,
                usage: enhancedResult.usage
              },
              duration: endTime - startTime,
              status: 'success' as const
            }
          : call
      ))
      
      setGeneratedHTML(enhancedResult.html)
      setShowOutput(true)
    } catch (err) {
      const endTime = Date.now()
      console.error('Generation error:', err)
      
      // Update API call with error details
      setApiCalls(prev => prev.map(call =>
        call.id === callId
          ? {
              ...call,
              response: {
                status: err instanceof Error && 'status' in err ? (err as any).status : 500,
                statusText: err instanceof Error && 'statusText' in err ? (err as any).statusText : 'Internal Server Error',
                data: '',
                error: err instanceof Error ? err.message : 'Failed to generate lens insights',
                headers: err instanceof Error && 'headers' in err ? (err as any).headers : {}
              },
              duration: endTime - startTime,
              status: 'error' as const
            }
          : call
      ))
      
      setError(err instanceof Error ? err.message : 'Failed to generate lens insights')
    } finally {
      setIsGenerating(false)
    }
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true)
    e.preventDefault()
  }

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return
    
    const container = document.querySelector('.split-container')
    if (!container) return
    
    const rect = container.getBoundingClientRect()
    const newWidth = ((e.clientX - rect.left) / rect.width) * 100
    
    // Constrain between 15% and 60%
    const constrainedWidth = Math.min(Math.max(newWidth, 15), 60)
    setLeftPanelWidth(constrainedWidth)
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  // Add event listeners for mouse move and up
  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
    }
  }, [isDragging])

  // Close sample selector when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element
      if (isSampleSelectorOpen && !target.closest('.sample-selector-container')) {
        setIsSampleSelectorOpen(false)
      }
    }

    if (isSampleSelectorOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isSampleSelectorOpen])

  return (
    <div className={`zen-creator-simple-mock min-h-screen bg-gray-50 ${className}`}>
      {/* Professional Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Left Side - Navigation & Title */}
            <div className="flex items-center gap-6">
              <button className="flex items-center gap-3 hover:opacity-80 transition-opacity duration-200">
                <svg className="w-8 h-8" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 28 H16 Q14 28 14 30 V70 Q14 72 16 72 H20" stroke="black" strokeWidth="5" fill="none" strokeLinecap="square"/>
                  <path d="M80 28 H84 Q86 28 86 30 V70 Q86 72 84 72 H80" stroke="black" strokeWidth="5" fill="none" strokeLinecap="square"/>
                  <polygon points="50,32 69,51 50,70 31,51" fill="none" stroke="black" strokeWidth="5.5"/>
                  <circle cx="50" cy="50" r="5.2" fill="black"/>
                </svg>
                <div>
                  <div className="text-left">
                    <h1 className="text-xl font-bold text-gray-900">SyntaxLens</h1>
                    <p className="text-xs text-gray-500 -mt-0.5">Interactive Schema Bridge</p>
                  </div>
                </div>
              </button>
            </div>

            {/* Right Side - Controls */}
            <div className="flex items-center gap-4">
              {/* Compact Language Context */}
              <div className="relative">
                <button 
                  onClick={() => setIsLanguageSelectorOpen(!isLanguageSelectorOpen)}
                  className="flex items-center gap-3 bg-slate-50 border border-gray-200 rounded-lg px-3 py-2 hover:bg-slate-100 transition-colors w-full"
                >
                  <div className="flex items-center gap-2 text-xs">
                    <div className="flex items-center gap-1">
                      <span className="font-medium text-blue-700">[{sourceLanguage}]</span>
                    </div>
                    <ArrowRight className="w-3 h-3 text-amber-500" />
                    <div className="flex items-center gap-1">
                      <span className="font-medium text-orange-700">{targetLanguage}</span>
                    </div>
                  </div>
                  <ChevronDown className="w-3 h-3 text-gray-400" />
                </button>
                
                {/* Language Selector Dropdown */}
                {isLanguageSelectorOpen && (
                  <div className="absolute top-full right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    <div className="p-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            I know (Source)
                          </label>
                          <select
                            value={sourceLanguage}
                            onChange={(e) => setSourceLanguage(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          >
                            {languages.map(lang => (
                              <option key={lang.code} value={lang.code}>
                                {lang.icon} {lang.code}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            I'm learning (Target)
                          </label>
                          <select
                            value={targetLanguage}
                            onChange={(e) => setTargetLanguage(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                          >
                            {languages.filter(l => l.code !== sourceLanguage).map(lang => (
                              <option key={lang.code} value={lang.code}>
                                {lang.icon} {lang.code}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <button
                        onClick={() => setIsLanguageSelectorOpen(false)}
                        className="mt-3 w-full px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
                      >
                        Done
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* AI Model Selector */}
              <ProviderSelector
                providers={providers}
                currentProvider={currentProvider}
                onProviderChange={switchProvider}
                isLoading={isLoadingProviders}
              />


              {/* Debug Panel Button */}
              <button
                onClick={() => setIsDebugOpen(true)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md transition-colors ${
                  apiCalls.length > 0
                    ? 'bg-purple-50 border border-purple-200 text-purple-700 hover:bg-purple-100'
                    : 'bg-gray-50 border border-gray-200 text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Bug className="w-3 h-3" />
                <span className="text-xs">Debug ({apiCalls.length})</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Resizable Split Pane Layout */}
      <div className="flex h-[calc(100vh-80px)] split-container">
        {/* Left Pane - Pure Code Editor */}
        <div 
          className="bg-white border-r border-gray-200 flex flex-col"
          style={{ width: `${leftPanelWidth}%` }}
        >
{/* Code Editor - Full Height */}
<div className="flex-1 flex flex-col p-4">{/* OUTER: Code Editor wrapper */}
  {/* Header row */}
  <div className="mb-3 flex items-center justify-between">
    {/* Left: label + sample selector */}
    <div className="flex items-center gap-3">
      <label className="text-xs font-medium text-gray-600 flex items-center gap-1.5">
        <Code className="w-3 h-3 text-blue-500" />
        Code Input
      </label>

      {/* Elegant Sample Selector */}
      <div className="relative sample-selector-container">
        <button
          onClick={() => setIsSampleSelectorOpen(!isSampleSelectorOpen)}
          className="flex items-center gap-1 text-xs text-gray-500 hover:text-orange-600 transition-colors duration-200 bg-gray-50 hover:bg-orange-50 px-2 py-1 rounded-md border border-gray-200 hover:border-orange-200"
          title="Code Examples"
        >
          <Layers className="w-3 h-3" />
          <ChevronDown className="w-3 h-3" />
        </button>

        {/* Zen Sample Dropdown */}
        {isSampleSelectorOpen && (
          <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
            <div className="p-4 bg-gradient-to-r from-orange-50 to-amber-50 border-b border-orange-100">
              <h4 className="text-sm font-medium text-gray-800 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-orange-500" />
                Examples
              </h4>
            </div>

            <div className="p-2 space-y-1 max-h-80 overflow-y-auto">
              {Object.entries(codeSamples).map(([key, sample]) => (
                <button
                  key={key}
                  onClick={() => {
                    setSelectedSample(key);
                    setIsSampleSelectorOpen(false);
                  }}
                  className={`w-full group relative overflow-hidden rounded-lg transition-all duration-200 ${
                    selectedSample === key
                      ? 'bg-orange-50 shadow-sm ring-1 ring-orange-200'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3 p-3">
                    {/* Content */}
                    <div className="flex-1 text-left">
                      <div className="flex items-center gap-2 mb-0.5">
                        <h5 className="font-medium text-gray-900 text-sm leading-tight">
                          {sample.name}
                        </h5>
                        <div
                          className={`w-2 h-2 rounded-full ${
                            sample.difficulty === 'Beginner'
                              ? 'bg-green-400'
                              : 'bg-blue-400'
                          }`}
                        />
                      </div>
                      <p className="text-xs text-gray-500 leading-relaxed">
                        {sample.description}
                      </p>
                    </div>

                    {/* Selection indicator */}
                    {selectedSample === key && (
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-full" />
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      {/* /Elegant Sample Selector */}
    </div>
    {/* /Left: label + sample selector */}

    {/* Right: Show Insights Button */}
    <button
      onClick={handleGenerate}
      disabled={isGenerating || !code.trim() || !aiService || !aiServiceEnhanced}
      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 flex items-center gap-1.5 shadow-sm ${
        isGenerating || !code.trim() || !aiService || !aiServiceEnhanced
          ? 'bg-gray-400 cursor-not-allowed text-white'
          : 'bg-orange-500 hover:bg-orange-600 text-white hover:shadow-md'
      }`}
    >
      {isGenerating ? (
        <>
          <Loader2 className="w-3 h-3 animate-spin" />
          <span>Analyzing...</span>
        </>
      ) : (
        <>
          <Sparkles className="w-3 h-3" />
          <span>Show Insights</span>
        </>
      )}
    </button>
    {/* /Right: Show Insights Button */}
  </div>
  {/* /Header row */}

  {/* Error Display */}
  {error && (
    <div className="mb-3 bg-red-50 border border-red-300 rounded-lg p-3">
      <p className="text-red-800 text-sm">{error}</p>
    </div>
  )}

  {/* Enhanced Code Editor - Perfect Balance */}
  <div className="flex-1 flex flex-col">
    <div className="flex-1 bg-gray-900 rounded-xl overflow-hidden flex flex-col">
      <div className="flex-1 flex">
        {/* Compact Line Numbers */}
        <div className="bg-gray-800 px-2 py-3 text-gray-400 text-xs font-mono select-none">
          <div className="space-y-1">
            {code.split('\n').map((_, i) => (
              <div key={i} className="text-right leading-5 w-6">
                {i + 1}
              </div>
            ))}
          </div>
        </div>

        {/* Code Content - Full Height */}
        <textarea
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder={`Paste your ${targetLanguage} code here...`}
          className="flex-1 bg-gray-900 text-gray-100 p-3 font-mono text-xs leading-5 resize-none border-0 outline-none placeholder-gray-500 focus:ring-1 focus:ring-orange-500/20"
          spellCheck={false}
          style={{ minHeight: '100%' }}
        />
      </div>

      {/* Compact Editor Footer */}
      <div className="bg-gray-800 border-t border-gray-700 px-3 py-1.5 flex items-center justify-between text-xs text-gray-500">
        <span className="text-xs">{code.split('\n').length} lines</span>
        <span className="px-1.5 py-0.5 bg-gray-700 rounded text-xs">
          {targetLanguage}
        </span>
      </div>
    </div>
  </div>
  {/* /Enhanced Code Editor - Perfect Balance */}
</div>
{/* /OUTER: Code Editor wrapper */}

        </div>

        {/* Resizable Divider - Ultra Subtle */}
        <div 
          className={`w-1 cursor-col-resize transition-all duration-200 relative group ${
            isDragging 
              ? 'bg-orange-500' 
              : 'bg-gray-200 hover:bg-gray-300'
          }`}
          onMouseDown={handleMouseDown}
        >
          {/* Drag Handle Visual Indicator - Nearly Invisible */}
          <div className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-1 h-8 rounded-full transition-all duration-200 ${
            isDragging 
              ? 'bg-orange-400 opacity-100' 
              : 'bg-gray-300 opacity-0 group-hover:opacity-60'
          }`}>
            <div className={`w-full h-0.5 rounded-full mt-1 transition-all duration-200 ${
              isDragging ? 'bg-white' : 'bg-gray-400'
            }`}></div>
            <div className={`w-full h-0.5 rounded-full mt-1.5 transition-all duration-200 ${
              isDragging ? 'bg-white' : 'bg-gray-400'
            }`}></div>
            <div className={`w-full h-0.5 rounded-full mt-1.5 transition-all duration-200 ${
              isDragging ? 'bg-white' : 'bg-gray-400'
            }`}></div>
          </div>
        </div>

        {/* Right Pane - Hero Output Panel */}
        <div 
          className="bg-gray-50 flex flex-col"
          style={{ width: `${100 - leftPanelWidth}%` }}
        >
          {showOutput ? (
            <>
              {/* Minimal Tab Navigation - Clean & Focused */}
              <div className="bg-white border-b border-gray-200">
                <div className="flex items-center justify-between px-6 py-2">
                  {/* Tab Navigation */}
                  <div className="flex space-x-6">
                    {[
                      { key: 'lens', label: '🔍 Hover Lens', desc: 'Interactive hover insights' },
                      { key: 'tutorial', label: '📚 Step-by-Step', desc: 'Learn with guided explanations' },
                      { key: 'compare', label: '⚡ Side-by-Side', desc: 'Compare both languages' },
                      { key: 'practice', label: '🎯 Try It', desc: 'Interactive code examples' }
                    ].map((tab) => (
                      <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        className={`py-2 text-sm font-medium border-b-2 transition-colors group ${
                          activeTab === tab.key
                            ? 'border-orange-500 text-orange-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                        title={tab.desc}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Compact Action Buttons */}
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-4 text-xs text-gray-500 mr-4">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        1.2s
                      </span>
                      <span className="flex items-center gap-1">
                        <Zap className="w-3 h-3" />
                        2.8K
                      </span>
                    </div>
                    <button className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded transition-colors" title="Copy">
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded transition-colors" title="Export">
                      <Download className="w-3.5 h-3.5" />
                    </button>
                    <button className="p-1.5 text-orange-500 hover:text-orange-600 hover:bg-orange-50 rounded transition-colors" title="Regenerate">
                      <RefreshCw className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Tab Content */}
              <div className="flex-1 overflow-auto">
                {activeTab === 'lens' && (
                  <SyntaxLensWidget 
                    className="h-full"
                    sourceLanguage={sourceLanguage}
                    targetLanguage={targetLanguage}
                    variant="modern"
                  >
                    <div className="p-8 bg-gray-900">
                      <div className="mb-4 text-center">
                        <p className="text-orange-400 text-sm font-medium mb-2">✨ Hover over any highlighted element below</p>
                        <p className="text-gray-400 text-xs">⌨️ Navigate: ← → • Depth: 1 2 3 • Pin: Space • Exit: Esc</p>
                      </div>
                      <div className="font-mono text-sm space-y-2 text-left">
                        {generatedHTML ? (
                          <div dangerouslySetInnerHTML={{ __html: generatedHTML }} />
                        ) : (
                          <div className="text-gray-500 text-center py-8">
                            Click "Show Insights" to see interactive analysis
                          </div>
                        )}
                      </div>
                    </div>
                  </SyntaxLensWidget>
                )}
                
                {activeTab === 'tutorial' && (
                  <div className="p-8 bg-white">
                    <div className="max-w-4xl mx-auto space-y-6">
                      <div className="text-center mb-8">
                        <h4 className="text-xl font-semibold text-gray-800 mb-2">📚 Step-by-Step Learning Path</h4>
                        <p className="text-gray-600">Build {targetLanguage} skills using your {sourceLanguage} foundation</p>
                      </div>
                      
                      <div className="space-y-6">
                        <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-r-lg">
                          <h5 className="font-semibold text-blue-800 mb-2">1. Function Declaration</h5>
                          <p className="text-blue-700 mb-3">In {sourceLanguage}: <code className="bg-blue-100 px-2 py-1 rounded">def fibonacci(n):</code></p>
                          <p className="text-blue-700">In {targetLanguage}: <code className="bg-blue-100 px-2 py-1 rounded">function fibonacci(n)</code></p>
                        </div>
                        
                        <div className="bg-purple-50 border-l-4 border-purple-400 p-6 rounded-r-lg">
                          <h5 className="font-semibold text-purple-800 mb-2">2. Conditional Logic</h5>
                          <p className="text-purple-700 mb-3">Both languages use similar if/else structure, but {targetLanguage} requires parentheses</p>
                        </div>
                        
                        <div className="bg-green-50 border-l-4 border-green-400 p-6 rounded-r-lg">
                          <h5 className="font-semibold text-green-800 mb-2">3. Return Statements</h5>
                          <p className="text-green-700">Notice how {targetLanguage} uses explicit braces and semicolons</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {activeTab === 'compare' && (
                  <div className="p-8 bg-gray-100">
                    <div className="max-w-6xl mx-auto">
                      <div className="text-center mb-8">
                        <h4 className="text-xl font-semibold text-gray-800 mb-2">⚡ Side-by-Side Learning</h4>
                        <p className="text-gray-600">See how the same concept expresses in both languages</p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-8">
                        <div className="bg-blue-50 rounded-xl p-6 border-2 border-blue-200">
                          <div className="flex items-center gap-2 mb-4">
                            <span className="text-2xl">🐍</span>
                            <h5 className="font-semibold text-blue-800">{sourceLanguage}</h5>
                          </div>
                          <pre className="bg-blue-100 p-4 rounded-lg text-sm font-mono text-blue-900 overflow-x-auto">
{`def fibonacci(n):
    if n <= 1:
        return n
    return fibonacci(n-1) + fibonacci(n-2)`}
                          </pre>
                        </div>
                        
                        <div className="bg-yellow-50 rounded-xl p-6 border-2 border-yellow-200">
                          <div className="flex items-center gap-2 mb-4">
                            <span className="text-2xl">⚡</span>
                            <h5 className="font-semibold text-yellow-800">{targetLanguage}</h5>
                          </div>
                          <pre className="bg-yellow-100 p-4 rounded-lg text-sm font-mono text-yellow-900 overflow-x-auto">
{`function fibonacci(n) {
    if (n <= 1) return n;
    return fibonacci(n - 1) + fibonacci(n - 2);
}`}
                          </pre>
                        </div>
                      </div>
                      
                      <div className="mt-8 bg-white rounded-xl p-6 shadow-sm">
                        <h5 className="font-semibold text-gray-800 mb-4">🔍 Key Differences</h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <div className="text-2xl mb-2">{ }</div>
                            <h6 className="font-medium text-gray-700">Braces</h6>
                            <p className="text-xs text-gray-600">{targetLanguage} uses curly braces</p>
                          </div>
                          <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <div className="text-2xl mb-2">;</div>
                            <h6 className="font-medium text-gray-700">Semicolons</h6>
                            <p className="text-xs text-gray-600">Optional but recommended</p>
                          </div>
                          <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <div className="text-2xl mb-2">( )</div>
                            <h6 className="font-medium text-gray-700">Parentheses</h6>
                            <p className="text-xs text-gray-600">Required around conditions</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {activeTab === 'practice' && (
                  <div className="p-8 bg-gradient-to-br from-green-50 to-blue-50">
                    <div className="max-w-4xl mx-auto text-center">
                      <h4 className="text-xl font-semibold text-gray-800 mb-4">🎯 Interactive Practice</h4>
                      <p className="text-gray-600 mb-8">Try modifying the code and see real-time feedback</p>
                      
                      <div className="bg-white rounded-xl p-8 shadow-lg mb-6">
                        <div className="bg-green-100 border border-green-300 rounded-lg p-4 mb-4">
                          <h5 className="font-semibold text-green-800 mb-2">✅ Challenge: Modify the Function</h5>
                          <p className="text-green-700 text-sm">Try changing the base case or add memoization</p>
                        </div>
                        
                        <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-4 mb-4">
                          <h5 className="font-semibold text-yellow-800 mb-2">💡 Hint</h5>
                          <p className="text-yellow-700 text-sm">What happens if you change n ≤ 1 to n ≤ 2?</p>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                          <button className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors">
                            🚀 Run Code
                          </button>
                          <button className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors">
                            💾 Save Example
                          </button>
                          <button className="bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 transition-colors">
                            🔄 Reset
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Hero Placeholder State */
            <div className="flex-1 flex items-center justify-center bg-gray-50">
              <div className="text-center p-12 max-w-lg">
                <div className="w-24 h-24 bg-orange-500 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-2xl">
                  <Sparkles className="w-12 h-12 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-gray-800 mb-4">
                  AI Lens Analysis
                </h3>
                <p className="text-gray-600 leading-relaxed mb-8 text-lg">
                  Enter your {targetLanguage} code and click "Show Me How" to discover connections with your {sourceLanguage} knowledge.
                </p>
                <div className="flex items-center justify-center gap-6 text-gray-500">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-orange-400 rounded-full animate-pulse"></div>
                    <span className="font-medium">Ultra-fast analysis</span>
                  </div>
                  <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-purple-400 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
                    <span className="font-medium">AI-powered insights</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Debug Panel */}
      <ZenSimpleDebugPanel
        isOpen={isDebugOpen}
        onClose={() => setIsDebugOpen(false)}
        apiCalls={apiCalls}
        onClear={() => setApiCalls([])}
      />

      {/* Enhanced Syntax Highlighting Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .keyword { color: #ff6b6b !important; font-weight: 600; }
          .type-keyword { color: #4ecdc4 !important; font-weight: 500; }
          .function, .method { color: #45b7d1 !important; font-weight: 500; }
          .variable { color: #ecf0f1 !important; }
          .operator { color: #a8e6cf !important; font-weight: 500; }
          .string { color: #feca57 !important; }
          .number { color: #ff9ff3 !important; }
          .comment { color: #95a5a6 !important; font-style: italic; }
          .bracket { color: #ffa726 !important; font-weight: 600; }
          
          .hoverable {
            display: inline-block;
            transition: all 0.2s ease;
            cursor: pointer;
            border-radius: 3px;
            padding: 1px 2px;
          }
          
          .hoverable:hover {
            background: rgba(59, 130, 246, 0.4) !important;
            filter: brightness(1.3);
            box-shadow: 0 0 8px rgba(59, 130, 246, 0.5);
            transform: translateY(-1px);
          }
        `
      }} />
    </div>
  )
}

export default ZenCreatorSimpleMOCK